<script setup>
import { ref, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Copy, Check, AlertTriangle } from 'lucide-vue-next'
import { useNotifications } from '../../composables/useNotifications'
import BaseModal from './BaseModal.vue'

const { t: $t } = useI18n()
const { showSuccess, showError } = useNotifications()

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  mnemonic: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['close'])

const copied = ref(false)
let copyTimeout = null

const close = () => {
  emit('close')
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(props.mnemonic)
    copied.value = true
    showSuccess($t('mnemonicModal.copied', 'Recovery phrase copied to clipboard'))

    // Reset after 2 seconds
    if (copyTimeout) {
      clearTimeout(copyTimeout)
    }
    copyTimeout = setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (error) {
    console.error('Failed to copy mnemonic:', error)
    showError($t('mnemonicModal.copyError', 'Failed to copy to clipboard'))
  }
}

// Cleanup timeout on unmount
onUnmounted(() => {
  if (copyTimeout) {
    clearTimeout(copyTimeout)
  }
})
</script>

<template>
  <BaseModal
    :open="open"
    max-width="640px"
    @close="close"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <AlertTriangle :size="24" class="text-orange-500" />
        <h2 class="text-xl font-bold text-gray-900">
          {{ $t('mnemonicModal.title', 'Backup Recovery Phrase') }}
        </h2>
      </div>
    </template>

    <!-- Warning -->
    <div class="warning-box">
      <p class="text-sm font-medium text-orange-800 mb-2">
        {{ $t('mnemonicModal.warning', '⚠️ Keep this phrase secure!') }}
      </p>
      <ul class="text-xs text-orange-700 space-y-1">
        <li>• {{ $t('mnemonicModal.warningLine1', 'Anyone with this phrase can access your funds') }}</li>
        <li>• {{ $t('mnemonicModal.warningLine2', 'Never share it with anyone') }}</li>
        <li>• {{ $t('mnemonicModal.warningLine3', 'Store it in a safe place offline') }}</li>
      </ul>
    </div>

    <!-- Mnemonic Display -->
    <div class="mnemonic-box">
      <div class="mnemonic-grid">
        <template v-for="(word, index) in mnemonic.split(' ')" :key="index">
          <div class="mnemonic-word">
            <span class="word-number">{{ index + 1 }}</span>
            <span class="word-text">{{ word }}</span>
          </div>
        </template>
      </div>
    </div>

    <template #footer>
      <div class="modal-actions">
        <button
          type="button"
          class="btn btn-secondary"
          @click="close"
        >
          {{ $t('common.close', 'Close') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :class="{ 'copied': copied }"
          @click="copyToClipboard"
        >
          <Component :is="copied ? Check : Copy" :size="16" />
          <span>{{ copied ? $t('common.copied', 'Copied!') : $t('common.copy', 'Copy') }}</span>
        </button>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.warning-box {
  @apply bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4;
}

.mnemonic-box {
  @apply bg-gray-50 border border-gray-200 rounded-lg p-4;
}

.mnemonic-grid {
  @apply grid grid-cols-2 sm:grid-cols-3 gap-2;
}

.mnemonic-word {
  @apply bg-white border border-gray-200 rounded-md px-3 py-2;
  @apply flex items-center gap-2;
}

.word-number {
  @apply text-xs font-medium text-gray-400 min-w-[20px];
}

.word-text {
  @apply text-sm font-mono font-medium text-gray-900;
}

.modal-actions {
  @apply flex items-center justify-end gap-3 w-full;
}

.btn {
  @apply px-4 py-2 rounded-lg font-medium text-sm;
  @apply border-none cursor-pointer;
  @apply transition-all duration-150;
  @apply flex items-center gap-2;
}

.btn-secondary {
  @apply bg-gray-100 text-gray-700;
  @apply hover:bg-gray-200;
}

.btn-primary {
  @apply bg-gradient-to-r from-blue-500 to-purple-600;
  @apply text-white shadow-md;
  @apply hover:shadow-lg;
  transform: translateY(0);
}

.btn-primary:hover {
  transform: translateY(-1px);
}

.btn-primary.copied {
  @apply bg-gradient-to-r from-green-500 to-green-600;
}

/* Mobile responsive */
@media (max-width: 640px) {
  .mnemonic-grid {
    @apply grid-cols-2;
  }
}
</style>
